import { useState, useEffect } from "react";
import { Identifier } from "react-admin";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { fetchDocumentResource } from "@/providers/dataProvider/documentDownload";

interface UseDownloadDocumentResult {
  data: Blob | null;
  isLoading: boolean;
  error: string | null;
}

export const useDownloadDocument = (documentId: Identifier): UseDownloadDocumentResult => {
  const [data, setData] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) return;

    // Create abort controller for cleanup
    const abortController = new AbortController();

    const loadDocument = async () => {
      setIsLoading(true);
      setError(null);
      // Clear the previous blob so switching documents can't paint the prior
      // one for a frame under the new title while the new fetch is in flight.
      setData(null);

      try {
        const apiUrl = getPartnerAwareApiUrl();
        const url = `${apiUrl}/documents/${documentId}/download`;

        // Partner-scoped, authenticated headers are built centrally so this raw
        // download can't silently drop X-Partner-ID (#5350). Not logged — the URL
        // carries the document id and this now fires for image/text previews too.
        const response = await fetchDocumentResource(url, { signal: abortController.signal });
        const blob = await response.blob();

        // Check if request was aborted before setting state
        if (!abortController.signal.aborted) {
          setData(blob);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }

        console.error("Error downloading document:", err);
        setError(err instanceof Error ? err.message : "Failed to load document");
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void loadDocument();

    // Cleanup function - abort request if component unmounts or effect re-runs
    return () => {
      abortController.abort();
    };
  }, [documentId]);

  return { data, isLoading, error };
};
